import { reduce, uniqBy } from "lodash-es";
import { useMemo } from "react";
import { IPermission, IPermissionGroup, IPermissionType } from "shared/types";

export const usePermissionsGroups = ({ permissions }: { permissions: IPermission[] }) => {
  const rootGroups = useMemo(
    () =>
      permissions
        ? uniqBy(
            reduce(
              permissions,
              (groups, permission) => {
                const root =
                  permission.permission.permissionGroup?.parentGroup ||
                  permission.permission.permissionGroup;

                if (root) {
                  groups.push(root);
                }

                return groups;
              },
              [] as IPermissionGroup[]
            ),
            "id"
          )
        : null,
    [permissions]
  );

  const childrenGroups = useMemo(
    () =>
      rootGroups
        ? reduce(
            permissions,
            (groups, permission) => {
              const root = permission.permission.permissionGroup?.parentGroup;
              const child = permission.permission.permissionGroup;
              const rootIndex = rootGroups.findIndex(({ id }) => id === root?.id);

              if (child && root && rootIndex > -1) {
                groups[rootIndex] = [...(groups[rootIndex] || []), child];
              }

              const childIndex = rootGroups.findIndex(({ id }) => id === child?.id);

              if (!root && childIndex > -1) {
                groups[childIndex] = [...(groups[childIndex] || []), permission.permission];
              }

              return groups;
            },
            rootGroups.map(() => []) as (IPermissionGroup | IPermissionType)[][]
          ).map(children => uniqBy(children, "id"))
        : null,
    [permissions, rootGroups]
  );

  return { rootGroups, childrenGroups };
};
